import styles from "./WaysToStyleCarousel.scss";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import { StylingLookProductFromServer, StylingLook } from "../../types/productTypes/stylingLookProduct";
import { getPrimaryImage, getHeroProduct } from "../../utils/product";
import Image from "../image/Image";
import PlusPill from "../plusPill/PlusPill";
import { getLookHeroImageUrl } from "../../utils/heroProductUtils";

interface WaysToStyleCarouselProps {
  stylingLooks: StylingLook[];
}

const renderStylingLookHeroImage = (
  look: StylingLook,
  index: number,
) => {
  const heroProduct = getHeroProduct(look.products)[0] ?? look.products[0];

  if (!heroProduct) {
    return null;
  }
  return (
    <Image
      key={`styling-look-hero-${index}`}
      url={getLookHeroImageUrl({ product: heroProduct, looksProducts: look.products })}
      alternateText={heroProduct?.name}
      type="carousel"
      width={640}
      additionalClasses=""
      loadingType="lazy"
      ariaHidden={false}
    />
  );
};

const renderStylingLookThumbnailImage = (
  product: StylingLookProductFromServer,
  index: number
) => (
  <Image
    key={`styling-look-thumbnail-${index}`}
    url={getPrimaryImage(product.images)?.url}
    alternateText={product.name}
    type="thumbnail"
    width={75}
    additionalClasses=""
    loadingType="lazy"
    ariaHidden={false}
  />
);

const getThumbnailsToShow = (products: StylingLookProductFromServer[]) => ({
  thumbnailsToShow: products.slice(0, 4),
  shouldShowPlusPill: products.length > 4,
  remainingCount: Math.max(0, products.length - 4),
});

export default function WaysToStyleCarousel({
  stylingLooks,
}: WaysToStyleCarouselProps) {
  const { getTranslation } = useRuntimeConstants() as {
    getTranslation: (key: string) => string;
  };

  if (stylingLooks.length === 0) {
    return null;
  }

  return (
    <div
      data-testid="waysToStyleCarousel"
      className={styles.carousel}
      aria-label={getTranslation("pdp_buy_the_look_carousel_accessibility")}
    >
      {stylingLooks.map((look, index) => {
        const { thumbnailsToShow, shouldShowPlusPill, remainingCount } =
          getThumbnailsToShow(look.products);

        return (
          <div
            key={`${look.products[0]?.id}-${index}`}
            className={styles.carouselItem}
          >
            <div className={styles.heroImage}>
              {renderStylingLookHeroImage(look, index)}
            </div>
            <div className={styles.thumbnailContainer}>
              {thumbnailsToShow.map((product, index) => (
                <div
                  key={`${product?.id}-${index}`}
                  className={styles.thumbnail}
                >
                  {renderStylingLookThumbnailImage(product, index)}
                </div>
              ))}
              {shouldShowPlusPill && (
                <PlusPill
                  number={remainingCount.toString()}
                  additionalClasses={styles.lightTheme}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
